import React, { useState } from "react"

import * as Yup from "yup"
import { useFormik } from "formik"
import { TextField, Button, Container, Typography, Card, Divider, Stack, CircularProgress } from "@mui/material"
import GoogleIcon from "@mui/icons-material/Google"
import { Link } from "react-router-dom"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import { useGoogleAuth } from "@/features/auth/hooks/useGoogleAuth"
import { getEmailSignInErrorMessage, signInWithEmail } from "@/shared/services/auth"

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().min(6, "Password too short").required("Password is required")
})

interface Props {
  onToggleSignUp?: () => void
}

const Login: React.FC<Props> = ({ onToggleSignUp }) => {
  const { showSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  const { signInWithGoogle, loading: googleLoading } = useGoogleAuth()

  const formik = useFormik({
    initialValues: {
      email: "",
      password: ""
    },
    validationSchema: LoginSchema,
    onSubmit: async values => {
      setLoading(true)
      try {
        await signInWithEmail(values.email, values.password)
      } catch (error) {
        console.log(error)
        showSnackbar(getEmailSignInErrorMessage(error), "error")
      } finally {
        setLoading(false)
      }
    }
  })

  return (
    <Container maxWidth={false} disableGutters sx={{ width: "100%" }}>
      <Card variant="outlined" sx={{ p: { xs: 3, sm: 3.5 }, mt: { xs: 2, sm: 2, md: 3 } }}>
        <Stack spacing={2.5} component="form" onSubmit={formik.handleSubmit}>
          <Typography variant="h5" component="h1" gutterBottom>
            Login
          </Typography>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            size="small"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <TextField
            fullWidth
            id="password"
            name="password"
            size="small"
            label="Password"
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />

          <Button
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            color="primary"
            variant="contained"
            fullWidth
            type="submit">
            Login
          </Button>
          <Typography variant="caption" component={Link} to="/forgot-password">
            Forgot password?
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={googleLoading ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
            onClick={() => void signInWithGoogle()}
            disabled={googleLoading}>
            Login with Google
          </Button>
          <Divider />
          <Button onClick={onToggleSignUp} color="secondary" variant="outlined">
            Sign Up
          </Button>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            By continuing, you agree to our{" "}
            <Typography component={Link} to="/terms" variant="caption">
              Terms of Use
            </Typography>{" "}
            and{" "}
            <Typography component={Link} to="/privacy" variant="caption">
              Privacy Policy
            </Typography>
            .
          </Typography>
        </Stack>
      </Card>
    </Container>
  )
}

export default Login
